/**
 * Per-language prose for the MarketingContent component. Kept in its
 * own file so i18n.ts (UI strings) stays focused on the short labels
 * and tooltips scattered across many components — the marketing
 * content is paragraph-heavy and benefits from being in one place per
 * language.
 *
 * Translations are functional rather than literary. SEO target keywords
 * are preserved per language so each translation ranks for the same
 * intent: "free private receipt scanner", "receipt to Excel/CSV/ZIP",
 * "OCR locally in browser". Native-speaker review welcome.
 *
 * The English copy is the source of truth — when something changes
 * here, the FAQ + HowTo JSON-LD in `index.html` mirrors English
 * verbatim. Phase 4 (per-language URLs) ships per-language `index.html`
 * shells so the structured data matches the rendered language too.
 */

import type { UiLangCode } from '../lib/i18n';

export interface MarketingStrings {
  hero: {
    title: string;
    subtitle: string;
  };
  howItWorksHeading: string;
  steps: Array<{ title: string; body: string }>;
  whyLocalHeading: string;
  whyLocalParagraphs: [string, string, string];
  useCasesHeading: string;
  useCases: Array<{ key: 'tax' | 'expense' | 'freelance' | 'split'; title: string; body: string }>;
  faqHeading: string;
  /** FAQ moved to its own /faq page; this is the landing-page teaser
   *  that links over to it. `body` uses a `{link}` token where the
   *  anchor (labelled with `linkLabel`, pointing at `href`) is spliced
   *  in. */
  faqCta: { body: string; linkLabel: string; href: string };
  feedback: {
    heading: string;
    /** Use the literal token `{email}` where the contact address should
     *  appear; the renderer splits on it and substitutes a mailto link. */
    body: string;
  };
}

const eng: MarketingStrings = {
  hero: {
    title: 'Free private receipt scanner — convert photos and PDFs to CSV, Excel, ZIP',
    subtitle:
      'Receipt Ripper turns receipt photos and PDFs into structured data — vendor, date, line items, totals, tax — entirely in your browser. No upload, no signup, no account. Free forever.',
  },
  howItWorksHeading: 'How it works',
  steps: [
    {
      title: '1. Drop the receipt',
      body:
        "Drag a photo (JPG, PNG, HEIC, even an iPhone HEIF), drop a PDF, or paste from your clipboard. Multiple receipts at once work too — drop a batch or even a ZIP archive of last quarter's photos.",
    },
    {
      title: '2. OCR runs in your browser',
      body:
        'Tesseract.js processes each receipt inside a Web Worker. Photos get auto-cropped, perspective-corrected, and read end-to-end on your device. Nothing uploads to any server.',
    },
    {
      title: '3. Review and export',
      body:
        'Edit any field that came through wrong, tag receipts with categories, then download CSV, Excel (.xlsx), or a ZIP that bundles a workbook plus every original image — ready for your bookkeeper.',
    },
  ],
  whyLocalHeading: 'Why local OCR matters',
  whyLocalParagraphs: [
    "Most receipt scanners work by uploading your image to a cloud OCR pipeline. Their server receives the photo, runs OCR remotely, and returns structured data. That works, but it means receipts sit on someone else's hardware: payment-card last-fours, store locations, purchase patterns, dates and times you were somewhere.",
    "Receipt Ripper takes the opposite stance. The OCR runs on your device. The parsed data lives in your browser tab and gets exported straight to your filesystem. There is no API call carrying receipt bytes — it's structurally impossible because the site's Content Security Policy refuses external network connections from anything that could carry receipt content.",
    "That makes Receipt Ripper a fit for anyone whose receipt data is sensitive: business owners with expense patterns they don't want indexed, contractors whose client billing is private, individuals doing tax prep, anyone in a high-regulation industry, and anyone who simply prefers their stuff stays theirs.",
  ],
  useCasesHeading: 'Use cases',
  useCases: [
    {
      key: 'tax',
      title: 'Tax preparation',
      body:
        "Year-end tax filing means turning a shoebox of receipts into deductible-expense rows. Receipt Ripper batches a quarter's worth in minutes. Subtotal, tax (VAT / IVA / TVA / GST), and total are extracted per receipt and summed in the Excel export — your accountant gets a workbook that pivots cleanly.",
    },
    {
      key: 'expense',
      title: 'Expense reports',
      body:
        'Reimbursable spend tracking on the road or after a client trip. Drag the receipts off your phone, edit the vendor names if OCR mangled them, hit Export. The ZIP bundle gives your finance team a workbook plus every original image attached — no separate "find the matching photo" step.',
    },
    {
      key: 'freelance',
      title: 'Freelancer bookkeeping',
      body:
        'Self-employed bookkeeping is a slow accumulation of small purchases. Drop each receipt in as you get it, tag with project or client, and the running batch sums the relevant totals. Toggle "Exclude tax from exports" if your jurisdiction frowns on handing tax-itemised data to third parties.',
    },
    {
      key: 'split',
      title: 'Splitting bills',
      body:
        'Group dinner where everyone wants their slice itemised. Receipt Ripper extracts each line item with quantity and price, so you can split by what each person actually ordered instead of dividing the total. Works on Spanish, French, German, Italian, Portuguese, and Dutch receipts as well as English.',
    },
  ],
  faqHeading: 'Frequently asked questions',
  faqCta: {
    body: 'Detailed answers about local OCR, supported languages, accuracy, exports, and troubleshooting now live on a dedicated page — {link}.',
    linkLabel: 'browse the full FAQ',
    href: '/faq.html',
  },
  feedback: {
    heading: 'Feature requests & tricky receipts',
    body:
      "Have an idea for a feature, or a receipt that didn't scan correctly? Email {email} — every problem receipt you share helps us tune the detection engine.",
  },
};

const spa: MarketingStrings = {
  hero: {
    title: 'Escáner de recibos privado y gratuito — convierte fotos y PDFs a CSV, Excel o ZIP',
    subtitle:
      'Receipt Ripper convierte fotos y PDFs de recibos en datos estructurados — proveedor, fecha, líneas, totales, IVA — completamente en tu navegador. Sin subidas, sin registro, sin cuenta. Gratis para siempre.',
  },
  howItWorksHeading: 'Cómo funciona',
  steps: [
    {
      title: '1. Suelta el recibo',
      body:
        'Arrastra una foto (JPG, PNG, HEIC, incluso HEIF de iPhone), suelta un PDF o pega desde el portapapeles. Varios recibos a la vez también funcionan — un lote o incluso un archivo ZIP con las fotos del último trimestre.',
    },
    {
      title: '2. El OCR corre en tu navegador',
      body:
        'Tesseract.js procesa cada recibo dentro de un Web Worker. Las fotos se recortan automáticamente, se corrige la perspectiva y se leen de extremo a extremo en tu dispositivo. Nada se sube a ningún servidor.',
    },
    {
      title: '3. Revisa y exporta',
      body:
        'Edita cualquier campo que haya salido mal, etiqueta los recibos con categorías y descarga CSV, Excel (.xlsx) o un ZIP que agrupa el libro de cálculo más cada imagen original — listo para tu contable.',
    },
  ],
  whyLocalHeading: 'Por qué importa el OCR local',
  whyLocalParagraphs: [
    'La mayoría de los escáneres de recibos funcionan subiendo tu imagen a una infraestructura OCR en la nube. Su servidor recibe la foto, ejecuta el OCR remotamente y devuelve los datos estructurados. Funciona, pero significa que tus recibos quedan en hardware ajeno: últimos cuatro dígitos de tarjetas, ubicaciones de tiendas, patrones de compra, fechas y horas en las que estuviste en algún sitio.',
    'Receipt Ripper toma la postura opuesta. El OCR se ejecuta en tu dispositivo. Los datos analizados viven en tu pestaña del navegador y se exportan directamente a tu sistema de archivos. No hay ninguna llamada a una API que transporte bytes de recibos — es estructuralmente imposible porque la Content Security Policy del sitio rechaza cualquier conexión de red externa que pudiera transportar contenido de recibos.',
    'Eso convierte a Receipt Ripper en una opción ideal para quien tenga datos de recibos sensibles: dueños de negocio con patrones de gasto que no quieren indexados, autónomos cuya facturación a clientes es privada, particulares haciendo la declaración de impuestos, profesionales en sectores muy regulados, y cualquiera que simplemente prefiera que sus cosas sigan siendo suyas.',
  ],
  useCasesHeading: 'Casos de uso',
  useCases: [
    {
      key: 'tax',
      title: 'Declaración de impuestos',
      body:
        'La declaración anual implica convertir una caja de recibos en filas de gastos deducibles. Receipt Ripper procesa el equivalente a un trimestre en minutos. La base imponible, el IVA y el total se extraen por recibo y se suman en la exportación a Excel — tu contable recibe un libro que pivota limpiamente.',
    },
    {
      key: 'expense',
      title: 'Notas de gastos',
      body:
        'Seguimiento de gastos reembolsables tras un viaje de cliente o de trabajo. Arrastra los recibos desde el móvil, corrige el nombre del proveedor si el OCR lo mutiló, pulsa Exportar. El ZIP da a tu equipo financiero un libro con cada imagen original adjunta — sin el paso "buscar la foto correspondiente".',
    },
    {
      key: 'freelance',
      title: 'Contabilidad de autónomos',
      body:
        'La contabilidad de un autónomo es la acumulación lenta de compras pequeñas. Suelta cada recibo en cuanto lo recibas, etiquétalo por proyecto o cliente, y el lote suma los totales relevantes. Activa "Excluir IVA de las exportaciones" si tu jurisdicción no recomienda entregar datos fiscalmente desglosados a terceros.',
    },
    {
      key: 'split',
      title: 'Dividir cuentas',
      body:
        'Cena de grupo en la que cada uno quiere su parte detallada. Receipt Ripper extrae cada línea con cantidad y precio, así puedes dividir por lo que cada persona pidió en lugar de partir el total. Funciona en recibos en español, francés, alemán, italiano, portugués y neerlandés además del inglés.',
    },
  ],
  faqHeading: 'Preguntas frecuentes',
  faqCta: {
    body: 'Las respuestas detalladas sobre el OCR local, idiomas compatibles, precisión, exportaciones y solución de problemas viven ahora en una página dedicada — {link}.',
    linkLabel: 'consulta la FAQ completa',
    href: '/es/faq.html',
  },
  feedback: {
    heading: 'Solicitudes de funciones y recibos difíciles',
    body:
      '¿Tienes una idea para una función, o un recibo que no se escaneó correctamente? Escríbenos a {email} — cada recibo problemático que compartas nos ayuda a mejorar el motor de detección.',
  },
};

const fra: MarketingStrings = {
  hero: {
    title: 'Scanner de reçus privé et gratuit — convertissez photos et PDF en CSV, Excel, ZIP',
    subtitle:
      'Receipt Ripper transforme photos et PDF de reçus en données structurées — commerçant, date, articles, totaux, TVA — entièrement dans votre navigateur. Sans téléchargement, sans inscription, sans compte. Gratuit pour toujours.',
  },
  howItWorksHeading: 'Comment ça marche',
  steps: [
    {
      title: '1. Déposez le reçu',
      body:
        "Glissez une photo (JPG, PNG, HEIC, ou même HEIF d'iPhone), déposez un PDF, ou collez depuis le presse-papiers. Plusieurs reçus à la fois fonctionnent aussi — déposez un lot ou même une archive ZIP des photos du dernier trimestre.",
    },
    {
      title: '2. L’OCR tourne dans votre navigateur',
      body:
        "Tesseract.js traite chaque reçu dans un Web Worker. Les photos sont recadrées automatiquement, leur perspective corrigée, et lues de bout en bout sur votre appareil. Rien n'est téléversé vers un serveur.",
    },
    {
      title: '3. Vérifiez et exportez',
      body:
        "Modifiez tout champ mal lu, taguez les reçus par catégorie, puis téléchargez CSV, Excel (.xlsx) ou un ZIP qui regroupe un classeur et chaque image originale — prêt pour votre comptable.",
    },
  ],
  whyLocalHeading: 'Pourquoi l’OCR local change tout',
  whyLocalParagraphs: [
    "La plupart des scanners de reçus fonctionnent en téléversant votre image vers un pipeline OCR cloud. Leur serveur reçoit la photo, exécute l'OCR à distance et renvoie les données structurées. Ça marche, mais cela veut dire que vos reçus se retrouvent sur le matériel de quelqu'un d'autre : les quatre derniers chiffres de la carte, l'emplacement des magasins, vos habitudes d'achat, les dates et heures où vous étiez quelque part.",
    "Receipt Ripper prend la position inverse. L'OCR tourne sur votre appareil. Les données analysées vivent dans l'onglet de votre navigateur et sont exportées directement vers votre système de fichiers. Aucune requête API ne transporte d'octets de reçus — c'est structurellement impossible parce que la Content Security Policy du site refuse les connexions réseau externes pour tout ce qui pourrait transporter du contenu de reçu.",
    "Cela rend Receipt Ripper adapté à toute personne dont les données de reçus sont sensibles : chefs d'entreprise dont les dépenses ne doivent pas être indexées, indépendants dont la facturation client est privée, particuliers préparant leurs impôts, professionnels en secteur fortement régulé, et toute personne qui préfère simplement que ses affaires restent les siennes.",
  ],
  useCasesHeading: 'Cas d’usage',
  useCases: [
    {
      key: 'tax',
      title: 'Déclaration fiscale',
      body:
        "La déclaration de fin d'année consiste à transformer une boîte à chaussures de reçus en lignes de dépenses déductibles. Receipt Ripper traite l'équivalent d'un trimestre en quelques minutes. Le sous-total, la TVA et le total sont extraits par reçu et additionnés dans l'export Excel — votre comptable obtient un classeur qui se pivote proprement.",
    },
    {
      key: 'expense',
      title: 'Notes de frais',
      body:
        'Suivi des dépenses remboursables après un déplacement client. Tirez les reçus de votre téléphone, corrigez le nom du commerçant si l’OCR l’a malmené, cliquez sur Exporter. Le ZIP donne à votre service financier un classeur avec chaque image originale jointe — sans étape « retrouver la photo correspondante ».',
    },
    {
      key: 'freelance',
      title: 'Comptabilité freelance',
      body:
        "La comptabilité d'un indépendant est l'accumulation lente de petits achats. Déposez chaque reçu au fur et à mesure, taguez par projet ou client, et le lot en cours additionne les totaux pertinents. Activez « Exclure la TVA des exports » si votre juridiction déconseille de remettre des données fiscalement détaillées à des tiers.",
    },
    {
      key: 'split',
      title: 'Partager une note',
      body:
        "Dîner de groupe où chacun veut sa part détaillée. Receipt Ripper extrait chaque ligne avec quantité et prix, ce qui permet de partager selon ce que chacun a réellement commandé plutôt que de diviser le total. Fonctionne sur les reçus en espagnol, français, allemand, italien, portugais et néerlandais aussi bien qu'en anglais.",
    },
  ],
  faqHeading: 'Questions fréquentes',
  faqCta: {
    body: 'Les réponses détaillées sur l\'OCR local, les langues supportées, la précision, les exports et le dépannage vivent désormais sur une page dédiée — {link}.',
    linkLabel: 'parcourir la FAQ complète',
    href: '/fr/faq.html',
  },
  feedback: {
    heading: 'Demandes de fonctionnalités et reçus difficiles',
    body:
      "Une idée de fonctionnalité, ou un reçu qui n'a pas été correctement scanné ? Écrivez-nous à {email} — chaque reçu problématique que vous partagez nous aide à améliorer le moteur de détection.",
  },
};

const deu: MarketingStrings = {
  hero: {
    title: 'Kostenloser privater Beleg-Scanner — Fotos und PDFs in CSV, Excel oder ZIP umwandeln',
    subtitle:
      'Receipt Ripper macht aus Beleg-Fotos und PDFs strukturierte Daten — Händler, Datum, Posten, Beträge, MwSt — komplett in Ihrem Browser. Kein Upload, keine Anmeldung, kein Konto. Dauerhaft kostenlos.',
  },
  howItWorksHeading: 'So funktioniert’s',
  steps: [
    {
      title: '1. Beleg ablegen',
      body:
        'Ziehen Sie ein Foto (JPG, PNG, HEIC, sogar iPhone HEIF), legen Sie ein PDF ab oder fügen Sie aus der Zwischenablage ein. Mehrere Belege auf einmal funktionieren auch — ein Stapel oder ein ZIP-Archiv mit den Fotos des letzten Quartals.',
    },
    {
      title: '2. OCR läuft im Browser',
      body:
        'Tesseract.js verarbeitet jeden Beleg in einem Web Worker. Fotos werden automatisch zugeschnitten, perspektivisch korrigiert und Ende-zu-Ende auf Ihrem Gerät gelesen. Nichts wird auf einen Server hochgeladen.',
    },
    {
      title: '3. Prüfen und exportieren',
      body:
        'Korrigieren Sie beliebige Felder, taggen Sie Belege mit Kategorien und laden Sie CSV, Excel (.xlsx) oder ein ZIP herunter, das Arbeitsmappe und Originalbild bündelt — fertig für Ihre Buchhaltung.',
    },
  ],
  whyLocalHeading: 'Warum lokale OCR den Unterschied macht',
  whyLocalParagraphs: [
    'Die meisten Beleg-Scanner laden Ihr Bild in eine Cloud-OCR-Pipeline. Deren Server bekommt das Foto, führt OCR remote aus und liefert strukturierte Daten zurück. Funktioniert, bedeutet aber, dass Ihre Belege auf fremder Hardware liegen: die letzten vier Stellen Ihrer Karte, Ladenstandorte, Kaufmuster, Daten und Uhrzeiten an denen Sie irgendwo waren.',
    'Receipt Ripper geht den umgekehrten Weg. OCR läuft auf Ihrem Gerät. Die geparsten Daten leben im Browser-Tab und werden direkt auf Ihre Festplatte exportiert. Es gibt keinen API-Aufruf, der Beleg-Bytes transportiert — strukturell unmöglich, weil die Content Security Policy externe Netzwerkverbindungen für alles ablehnt, was Beleg-Inhalt tragen könnte.',
    'Damit ist Receipt Ripper passend für alle, deren Beleg-Daten sensibel sind: Unternehmer mit nicht-indexierbaren Ausgabenmustern, Selbstständige mit privater Kundenabrechnung, Privatpersonen bei der Steuererklärung, regulierte Branchen, und alle, die einfach möchten, dass ihre Sachen ihre Sachen bleiben.',
  ],
  useCasesHeading: 'Anwendungsfälle',
  useCases: [
    {
      key: 'tax',
      title: 'Steuererklärung',
      body:
        'Jahresabschluss heißt, einen Schuhkarton voll Belege in Zeilen abzugsfähiger Ausgaben zu verwandeln. Receipt Ripper verarbeitet ein Quartal in Minuten. Zwischensumme, MwSt und Summe werden pro Beleg extrahiert und im Excel-Export aufaddiert — Ihr Steuerberater bekommt eine Arbeitsmappe, die sich sauber pivotieren lässt.',
    },
    {
      key: 'expense',
      title: 'Spesenabrechnung',
      body:
        'Erstattungsfähige Ausgaben unterwegs oder nach einem Kundenbesuch. Belege vom Handy ziehen, Händlernamen korrigieren wenn OCR sie verstümmelt hat, Export drücken. Das ZIP gibt Ihrem Finance-Team eine Arbeitsmappe samt aller Originalbilder — kein zusätzliches "passendes Foto suchen".',
    },
    {
      key: 'freelance',
      title: 'Freelance-Buchhaltung',
      body:
        'Selbstständigen-Buchhaltung ist die langsame Anhäufung kleiner Käufe. Belege ablegen sobald sie kommen, mit Projekt oder Kunden taggen, und der laufende Stapel summiert die relevanten Beträge. „MwSt aus Exporten ausschließen" aktivieren, wenn Ihre Rechtslage Steuer-itemisierte Daten an Dritte ungern sieht.',
    },
    {
      key: 'split',
      title: 'Rechnungen aufteilen',
      body:
        'Gruppen-Essen, bei dem jeder seinen Anteil itemisiert haben will. Receipt Ripper extrahiert jede Zeile mit Menge und Preis, sodass Sie nach dem aufteilen können, was jeder bestellt hat — statt die Summe zu teilen. Funktioniert mit Belegen auf Spanisch, Französisch, Deutsch, Italienisch, Portugiesisch und Niederländisch ebenso wie Englisch.',
    },
  ],
  faqHeading: 'Häufig gestellte Fragen',
  faqCta: {
    body: 'Ausführliche Antworten zu lokalem OCR, unterstützten Sprachen, Genauigkeit, Exporten und Fehlerbehebung leben jetzt auf einer eigenen Seite — {link}.',
    linkLabel: 'die vollständige FAQ durchstöbern',
    href: '/de/faq.html',
  },
  feedback: {
    heading: 'Feature-Wünsche & schwierige Belege',
    body:
      'Haben Sie eine Idee für eine Funktion oder einen Beleg, der nicht korrekt gescannt wurde? Schreiben Sie uns an {email} — jeder problematische Beleg, den Sie teilen, hilft uns, die Erkennungs-Engine zu verbessern.',
  },
};

const nld: MarketingStrings = {
  hero: {
    title: 'Gratis privé bonscanner — converteer foto’s en PDF’s naar CSV, Excel of ZIP',
    subtitle:
      'Receipt Ripper zet bonfoto’s en PDF’s om naar gestructureerde data — leverancier, datum, regels, totalen, BTW — volledig in je browser. Geen upload, geen registratie, geen account. Voor altijd gratis.',
  },
  howItWorksHeading: 'Hoe het werkt',
  steps: [
    {
      title: '1. Sleep de bon erin',
      body:
        'Sleep een foto (JPG, PNG, HEIC, ook iPhone HEIF), zet een PDF neer of plak vanaf het klembord. Meerdere bonnen tegelijk kan ook — een batch of zelfs een ZIP-archief met de foto’s van het vorige kwartaal.',
    },
    {
      title: '2. OCR draait in je browser',
      body:
        'Tesseract.js verwerkt elke bon in een Web Worker. Foto’s worden automatisch bijgesneden, het perspectief gecorrigeerd en end-to-end op je apparaat gelezen. Niets wordt naar een server geüpload.',
    },
    {
      title: '3. Controleer en exporteer',
      body:
        'Pas verkeerd gelezen velden aan, label bonnen met categorieën en download CSV, Excel (.xlsx) of een ZIP met werkmap plus elk origineel beeld — klaar voor je boekhouder.',
    },
  ],
  whyLocalHeading: 'Waarom lokale OCR ertoe doet',
  whyLocalParagraphs: [
    'De meeste bonscanners werken door je beeld naar een cloud-OCR-pipeline te uploaden. Hun server ontvangt de foto, draait OCR op afstand en geeft gestructureerde data terug. Werkt, maar betekent dat je bonnen op andermans hardware staan: laatste vier cijfers van kaarten, winkellocaties, koopgewoontes, data en tijden waarop je ergens was.',
    'Receipt Ripper kiest het tegenovergestelde. OCR draait op je apparaat. De geparseerde data leeft in je browsertabblad en wordt direct naar je bestandssysteem geëxporteerd. Er is geen API-call die bon-bytes vervoert — structureel onmogelijk, omdat het Content Security Policy van de site externe netwerkverbindingen afwijst voor alles wat boninhoud zou kunnen vervoeren.',
    'Daarmee past Receipt Ripper bij iedereen wiens bondata gevoelig is: ondernemers met uitgavenpatronen die niet geïndexeerd mogen worden, zelfstandigen met privé klant-facturatie, particulieren met belastingaangifte, mensen in sterk gereguleerde sectoren, en iedereen die simpelweg liever wil dat zijn spullen van hem blijven.',
  ],
  useCasesHeading: 'Toepassingen',
  useCases: [
    {
      key: 'tax',
      title: 'Belastingaangifte',
      body:
        'Jaarafsluiting betekent een schoenendoos vol bonnen omzetten naar aftrekposten. Receipt Ripper verwerkt een kwartaal in minuten. Subtotaal, BTW en totaal worden per bon geëxtraheerd en in de Excel-export gesommeerd — je boekhouder krijgt een werkmap die schoon pivoteert.',
    },
    {
      key: 'expense',
      title: 'Declaraties',
      body:
        'Onkostendeclaratie onderweg of na een klantbezoek. Sleep de bonnen van je telefoon, corrigeer de leveranciersnaam als OCR hem verminkt heeft, druk op Exporteren. Het ZIP geeft je financeteam een werkmap plus elk origineel beeld erbij — geen aparte "bijbehorende foto zoeken"-stap.',
    },
    {
      key: 'freelance',
      title: 'ZZP-boekhouding',
      body:
        'Boekhouding voor zelfstandigen is een trage stapel kleine aankopen. Zet elke bon erin zodra je hem krijgt, label per project of klant, en de lopende batch sommeert de relevante totalen. Zet "BTW uitsluiten van exports" aan als jouw jurisdictie het delen van BTW-uitgesplitste data met derden afraadt.',
    },
    {
      key: 'split',
      title: 'Rekening delen',
      body:
        'Groepsdiner waar iedereen zijn deel uitgesplitst wil. Receipt Ripper haalt elke regel met aantal en prijs eruit, zodat je kunt verdelen op wat ieder daadwerkelijk besteld heeft in plaats van het totaal te delen. Werkt op bonnen in het Spaans, Frans, Duits, Italiaans, Portugees en Nederlands net zo goed als Engels.',
    },
  ],
  faqHeading: 'Veelgestelde vragen',
  faqCta: {
    body: 'Uitgebreide antwoorden over lokale OCR, ondersteunde talen, nauwkeurigheid, exports en probleemoplossing staan nu op een aparte pagina — {link}.',
    linkLabel: 'blader door de volledige FAQ',
    href: '/nl/faq.html',
  },
  feedback: {
    heading: 'Functiewensen & lastige bonnetjes',
    body:
      'Heb je een idee voor een functie of een bonnetje dat niet goed werd gescand? Mail ons op {email} — elk probleembonnetje dat je deelt helpt ons de detectie te verbeteren.',
  },
};

const ita: MarketingStrings = {
  hero: {
    title: 'Scanner di scontrini privato e gratuito — converti foto e PDF in CSV, Excel, ZIP',
    subtitle:
      'Receipt Ripper trasforma foto e PDF di scontrini in dati strutturati — esercente, data, voci, totali, IVA — interamente nel tuo browser. Nessun upload, nessuna registrazione, nessun account. Gratis per sempre.',
  },
  howItWorksHeading: 'Come funziona',
  steps: [
    {
      title: '1. Trascina lo scontrino',
      body:
        'Trascina una foto (JPG, PNG, HEIC, anche HEIF di iPhone), rilascia un PDF o incolla dagli appunti. Anche più scontrini insieme — un batch o un archivio ZIP con le foto del trimestre scorso.',
    },
    {
      title: '2. L’OCR gira nel tuo browser',
      body:
        'Tesseract.js elabora ogni scontrino in un Web Worker. Le foto vengono ritagliate automaticamente, la prospettiva corretta e lette end-to-end sul tuo dispositivo. Niente viene caricato su alcun server.',
    },
    {
      title: '3. Controlla ed esporta',
      body:
        'Modifica eventuali campi sbagliati, etichetta gli scontrini per categoria e scarica CSV, Excel (.xlsx) o un ZIP che impacchetta una cartella di lavoro più ogni immagine originale — pronto per il tuo commercialista.',
    },
  ],
  whyLocalHeading: 'Perché l’OCR locale fa la differenza',
  whyLocalParagraphs: [
    'La maggior parte degli scanner di scontrini funziona caricando l\'immagine su una pipeline OCR cloud. Il loro server riceve la foto, esegue OCR remoto e restituisce dati strutturati. Funziona, ma vuol dire che gli scontrini stanno sull\'hardware di qualcun altro: ultime quattro cifre delle carte, posizioni dei negozi, abitudini di spesa, date e orari in cui eri da qualche parte.',
    'Receipt Ripper prende la posizione opposta. L\'OCR gira sul tuo dispositivo. I dati estratti vivono nella scheda del browser e vengono esportati direttamente sul tuo filesystem. Non c\'è alcuna chiamata API che porti byte di scontrini — è strutturalmente impossibile perché la Content Security Policy del sito rifiuta connessioni di rete esterne per qualsiasi cosa che possa trasportare contenuto di scontrini.',
    'Questo rende Receipt Ripper adatto a chiunque abbia dati di scontrini sensibili: imprenditori con pattern di spesa che non vogliono indicizzati, professionisti la cui fatturazione cliente è privata, privati che fanno la dichiarazione, lavoratori in settori altamente regolati, e chiunque preferisca semplicemente che le sue cose restino sue.',
  ],
  useCasesHeading: 'Casi d’uso',
  useCases: [
    {
      key: 'tax',
      title: 'Dichiarazione dei redditi',
      body:
        'La dichiarazione di fine anno significa trasformare una scatola di scontrini in righe di spese deducibili. Receipt Ripper processa l\'equivalente di un trimestre in minuti. Imponibile, IVA e totale vengono estratti per scontrino e sommati nell\'export Excel — il tuo commercialista riceve una cartella di lavoro che pivota in modo pulito.',
    },
    {
      key: 'expense',
      title: 'Note spese',
      body:
        'Tracciamento di spese rimborsabili in trasferta o dopo una visita cliente. Trascina gli scontrini dal telefono, correggi i nomi degli esercenti se l\'OCR li ha massacrati, premi Esporta. Il ZIP dà al tuo team finance una cartella di lavoro più ogni immagine originale allegata — senza il passaggio "trova la foto corrispondente".',
    },
    {
      key: 'freelance',
      title: 'Contabilità freelance',
      body:
        'La contabilità di un libero professionista è una lenta accumulazione di piccoli acquisti. Inserisci ogni scontrino appena lo ricevi, etichetta per progetto o cliente, e il batch in corso somma i totali rilevanti. Attiva "Escludi IVA dalle esportazioni" se la tua giurisdizione vede male la consegna a terzi di dati IVA dettagliati.',
    },
    {
      key: 'split',
      title: 'Dividere i conti',
      body:
        'Cena di gruppo dove ognuno vuole la sua parte dettagliata. Receipt Ripper estrae ogni voce con quantità e prezzo, così puoi dividere per ciò che ognuno ha effettivamente ordinato invece di spezzare il totale. Funziona con scontrini in spagnolo, francese, tedesco, italiano, portoghese e olandese oltre all\'inglese.',
    },
  ],
  faqHeading: 'Domande frequenti',
  faqCta: {
    body: 'Le risposte dettagliate su OCR locale, lingue supportate, precisione, esportazioni e risoluzione dei problemi vivono ora su una pagina dedicata — {link}.',
    linkLabel: 'sfoglia la FAQ completa',
    href: '/it/faq.html',
  },
  feedback: {
    heading: 'Richieste di funzionalità e ricevute difficili',
    body:
      "Hai un'idea per una funzionalità o una ricevuta che non è stata scansionata correttamente? Scrivici a {email} — ogni ricevuta problematica che condividi ci aiuta a migliorare il motore di rilevamento.",
  },
};

const por: MarketingStrings = {
  hero: {
    title: 'Scanner de recibos privado e gratuito — converta fotos e PDFs em CSV, Excel ou ZIP',
    subtitle:
      'Receipt Ripper transforma fotos e PDFs de recibos em dados estruturados — comerciante, data, linhas, totais, IVA — inteiramente no seu navegador. Sem upload, sem registo, sem conta. Sempre grátis.',
  },
  howItWorksHeading: 'Como funciona',
  steps: [
    {
      title: '1. Largue o recibo',
      body:
        'Arraste uma foto (JPG, PNG, HEIC, mesmo HEIF do iPhone), largue um PDF ou cole da área de transferência. Vários recibos de uma vez também — um lote ou mesmo um arquivo ZIP com as fotos do último trimestre.',
    },
    {
      title: '2. O OCR corre no seu navegador',
      body:
        'O Tesseract.js processa cada recibo dentro de um Web Worker. As fotos são recortadas automaticamente, a perspetiva corrigida e lidas de ponta a ponta no seu dispositivo. Nada é enviado para qualquer servidor.',
    },
    {
      title: '3. Reveja e exporte',
      body:
        'Edite qualquer campo que tenha vindo errado, etiquete recibos por categoria e descarregue CSV, Excel (.xlsx) ou um ZIP que junta uma pasta de trabalho mais cada imagem original — pronto para o seu contabilista.',
    },
  ],
  whyLocalHeading: 'Porque é que o OCR local importa',
  whyLocalParagraphs: [
    'A maioria dos scanners de recibos funcionam enviando a sua imagem para uma pipeline de OCR na nuvem. O servidor deles recebe a foto, executa OCR remotamente e devolve os dados estruturados. Funciona, mas significa que os recibos ficam no hardware de outra pessoa: últimos quatro dígitos do cartão, localizações de lojas, padrões de compra, datas e horas em que esteve em algum sítio.',
    'O Receipt Ripper toma a posição oposta. O OCR corre no seu dispositivo. Os dados analisados vivem no separador do navegador e são exportados diretamente para o seu sistema de ficheiros. Não há chamada de API a transportar bytes de recibos — é estruturalmente impossível porque a Content Security Policy do site recusa ligações de rede externas para tudo o que possa transportar conteúdo de recibo.',
    'Isso torna o Receipt Ripper adequado para quem tenha dados de recibos sensíveis: empresários com padrões de despesa que não querem indexados, freelancers cuja faturação a clientes é privada, particulares a tratar de impostos, profissionais em setores muito regulados, e qualquer pessoa que simplesmente prefira que as suas coisas continuem a ser suas.',
  ],
  useCasesHeading: 'Casos de uso',
  useCases: [
    {
      key: 'tax',
      title: 'Declaração de impostos',
      body:
        'A declaração de fim de ano significa transformar uma caixa de sapatos cheia de recibos em linhas de despesas dedutíveis. O Receipt Ripper processa o equivalente a um trimestre em minutos. Subtotal, IVA e total são extraídos por recibo e somados na exportação Excel — o seu contabilista recebe uma pasta de trabalho que pivota de forma limpa.',
    },
    {
      key: 'expense',
      title: 'Notas de despesa',
      body:
        'Acompanhamento de despesas reembolsáveis em viagem ou após uma visita a cliente. Arraste os recibos do telemóvel, corrija o nome do comerciante se o OCR o estragou, prima Exportar. O ZIP dá à sua equipa financeira uma pasta de trabalho mais cada imagem original anexada — sem o passo separado de "encontrar a foto correspondente".',
    },
    {
      key: 'freelance',
      title: 'Contabilidade freelancer',
      body:
        'A contabilidade de um freelancer é a acumulação lenta de pequenas compras. Largue cada recibo à medida que o recebe, etiquete por projeto ou cliente, e o lote em curso soma os totais relevantes. Ative "Excluir IVA das exportações" se a sua jurisdição não vê com bons olhos a entrega a terceiros de dados detalhados de IVA.',
    },
    {
      key: 'split',
      title: 'Dividir contas',
      body:
        'Jantar de grupo onde toda a gente quer a sua parte detalhada. O Receipt Ripper extrai cada linha com quantidade e preço, para poder dividir por aquilo que cada pessoa realmente pediu em vez de partir o total. Funciona em recibos em espanhol, francês, alemão, italiano, português e neerlandês para além do inglês.',
    },
  ],
  faqHeading: 'Perguntas frequentes',
  faqCta: {
    body: 'As respostas detalhadas sobre OCR local, idiomas suportados, precisão, exportações e resolução de problemas vivem agora numa página dedicada — {link}.',
    linkLabel: 'consulte a FAQ completa',
    href: '/pt/faq.html',
  },
  feedback: {
    heading: 'Pedidos de funcionalidades e recibos difíceis',
    body:
      'Tem uma ideia para uma funcionalidade ou um recibo que não foi digitalizado corretamente? Envie-nos um email para {email} — cada recibo problemático que partilha ajuda-nos a melhorar o motor de deteção.',
  },
};

export const MARKETING_BY_LANG: Record<UiLangCode, MarketingStrings> = {
  eng,
  spa,
  fra,
  deu,
  nld,
  ita,
  por,
};
